import React from 'react';

const Container = ({ 
  children, 
  size = 'standard',
  className = '', 
  padding = 'default',
  maxWidth = true,
  ...props 
}) => {
  const sizeClasses = {
    full: "w-full",
    narrow: "max-w-4xl",
    standard: "max-w-7xl",
    wide: "max-w-[1400px]",
  };

  const paddingClasses = {
    none: "",
    small: "px-4 sm:px-6",
    default: "px-4 sm:px-6 lg:px-8",
    large: "px-6 sm:px-8 lg:px-12",
  };

  const maxWidthClasses = maxWidth ? "mx-auto" : "";

  const classes = [
    sizeClasses[size],
    maxWidthClasses,
    paddingClasses[padding],
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
};

// Container sub-components for consistent structure
Container.Section = ({ 
  children, 
  className = '', 
  padding = 'content',
  background = 'white',
  ...props 
}) => {
  const paddingClasses = {
    none: "",
    minimal: "py-6 lg:py-12",
    compact: "py-8 lg:py-16",
    content: "py-16 lg:py-24",
    hero: "py-20 lg:py-32",
  };

  const backgroundClasses = {
    white: "bg-white",
    light: "bg-light",
    dark: "bg-secondary text-white",
    gradient: "bg-gradient-to-br from-light to-white",
    primary: "bg-primary text-white",
  };

  const classes = [
    paddingClasses[padding],
    backgroundClasses[background],
    className
  ].filter(Boolean).join(' ');

  return (
    <section className={classes} {...props}>
      {children}
    </section>
  );
};

Container.Grid = ({ 
  children, 
  columns = '3',
  className = '', 
  gap = 'default',
  ...props 
}) => {
  const columnClasses = {
    "1": "grid grid-cols-1",
    "2": "grid grid-cols-1 md:grid-cols-2",
    "3": "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    "4": "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
    "auto-fit": "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
  };

  const gapClasses = {
    small: "gap-4",
    default: "gap-6 lg:gap-8",
    large: "gap-8 lg:gap-12",
  };

  const classes = [
    columnClasses[columns],
    gapClasses[gap],
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
};

Container.Flex = ({ 
  children, 
  direction = 'row',
  justify = 'start',
  align = 'center',
  wrap = false,
  gap = 'default',
  className = '', 
  ...props 
}) => {
  const directionClasses = {
    row: "flex-row",
    col: "flex-col",
    "row-reverse": "flex-row-reverse",
    "col-reverse": "flex-col-reverse",
  };

  const justifyClasses = {
    start: "justify-start",
    end: "justify-end",
    center: "justify-center",
    between: "justify-between",
    around: "justify-around",
    evenly: "justify-evenly",
  };

  const alignClasses = {
    start: "items-start",
    end: "items-end",
    center: "items-center",
    baseline: "items-baseline",
    stretch: "items-stretch",
  };

  const wrapClasses = wrap ? "flex-wrap" : "flex-nowrap";

  const gapClasses = {
    none: "",
    small: "gap-2",
    default: "gap-4",
    large: "gap-6",
    xl: "gap-8",
  };

  const classes = [
    "flex",
    directionClasses[direction],
    justifyClasses[justify],
    alignClasses[align],
    wrapClasses,
    gapClasses[gap],
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
};

export default Container;
